import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle, X, Clock, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { meetingService } from '../../services/meetingService';

interface MeetingStatusDropdownProps {
  currentStatus: string;
  meetingId: string;
  meeting?: any;
  onStatusChanged: () => void;
}

const statusOptions = [
  {
    value: 'upcoming',
    label: 'Upcoming',
    icon: Clock,
    bgClass: 'bg-[#1875FF]/10',
    textClass: 'text-[#1875FF]',
    borderClass: 'border-[#1875FF]/30',
    hoverBg: 'hover:bg-[#1875FF]/5',
  },
  {
    value: 'attended',
    label: 'Attended',
    icon: CheckCircle,
    bgClass: 'bg-[#05E0D1]/10',
    textClass: 'text-[#05E0D1]',
    borderClass: 'border-[#05E0D1]/30',
    hoverBg: 'hover:bg-[#05E0D1]/5',
  },
  {
    value: 'missed',
    label: 'Missed',
    icon: X,
    bgClass: 'bg-red-50',
    textClass: 'text-red-700',
    borderClass: 'border-red-200',
    hoverBg: 'hover:bg-red-50',
  },
  {
    value: 'cancelled',
    label: 'Cancelled',
    icon: X,
    bgClass: 'bg-gray-50',
    textClass: 'text-gray-700',
    borderClass: 'border-gray-200',
    hoverBg: 'hover:bg-gray-50',
  },
];

const MeetingStatusDropdown: React.FC<MeetingStatusDropdownProps> = ({
  currentStatus,
  meetingId,
  meeting,
  onStatusChanged,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const normalizedStatus = (currentStatus || '').toLowerCase();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 260;
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      setOpenUpward(spaceBelow < dropdownHeight);
    }
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isUpdating) setIsOpen(!isOpen);
  };

  const handleStatusClick = async (e: React.MouseEvent, newStatus: string) => {
    e.stopPropagation();
    if (newStatus === normalizedStatus || isUpdating) return;

    setIsUpdating(true);
    try {
      const hasExternalIds = meeting?.calendar_event_id || meeting?.conference_meeting_id;

      if (newStatus === 'cancelled' && hasExternalIds) {
        const result = await meetingService.cancelMeeting({
          client_id: meeting.client_id,
          user_id: meeting.user_id,
          lead_id: meeting.lead_id,
        });
        if (!result.success) {
          console.error('Cancel API failed, falling back to direct update:', result.error);
          const { error } = await supabase
            .from('meetings_booked')
            .update({ meeting_status: 'Cancelled', mtg_invite_cancelled: true })
            .eq('meeting_id', meetingId);
          if (error) throw error;
        }

        if (meeting?.lead_id && meeting?.client_id && meeting?.user_id) {
          await meetingService.prepareMeetingReminders({
            lead_id: meeting.lead_id,
            client_id: meeting.client_id,
            user_id: meeting.user_id,
            type: 'cancelled',
          });
        }
      } else {
        const { error } = await supabase
          .from('meetings_booked')
          .update({ meeting_status: newStatus.charAt(0).toUpperCase() + newStatus.slice(1) })
          .eq('meeting_id', meetingId);
        if (error) throw error;
      }

      onStatusChanged();
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to update meeting status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const currentConfig = statusOptions.find(o => o.value === normalizedStatus) || statusOptions[0];
  const CurrentIcon = currentConfig.icon;

  return (
    <div ref={dropdownRef} className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={handleToggle}
        disabled={isUpdating}
        className={`
          inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border-2
          transition-all duration-300 cursor-pointer group
          ${currentConfig.bgClass} ${currentConfig.textClass} ${currentConfig.borderClass}
          ${isUpdating ? 'opacity-50 cursor-wait' : 'hover:shadow-lg hover:scale-105'}
        `}
        title="Click to change status"
      >
        {isUpdating ? (
          <div className="mr-1 w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : (
          <CurrentIcon size={12} className="mr-1 transition-transform group-hover:scale-110" />
        )}
        {currentConfig.label}
      </button>

      {isOpen && !isUpdating && (
        <div
          className={`absolute z-50 w-40 bg-white border-2 border-[#1875FF]/20 rounded-xl shadow-2xl shadow-[#1875FF]/20 overflow-hidden animate-fadeIn left-1/2 -translate-x-1/2 ${
            openUpward ? 'bottom-full mb-2' : 'mt-2'
          }`}
        >
          <div className="py-1">
            {statusOptions.map((option, index) => {
              const OptionIcon = option.icon;
              const isActive = normalizedStatus === option.value;
              return (
                <button
                  key={option.value}
                  onClick={(e) => handleStatusClick(e, option.value)}
                  className={`
                    w-full px-4 py-2.5 text-left flex items-center justify-between
                    transition-all duration-200 text-sm font-medium
                    ${index > 0 ? 'border-t border-gray-100' : ''}
                    ${isActive
                      ? `${option.bgClass} ${option.textClass}`
                      : `text-gray-700 ${option.hoverBg}`
                    }
                  `}
                >
                  <div className="flex items-center">
                    <OptionIcon size={14} className="mr-2" />
                    {option.label}
                  </div>
                  {isActive && <Check size={14} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingStatusDropdown;
